import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Download, LogOut, Users, FileText, 
  Trash2, Plus, RefreshCw, Search,
  ChevronDown, ChevronUp, X, Building2, Star, FileDown, Calendar, Clock, Briefcase, Wrench, Video, UserPlus, Settings, Link as LinkIcon,
  Palette, Upload, ExternalLink, Copy, BarChart3, Shield, AlertTriangle, Monitor, Smartphone, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('commercial');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '' });
  const [reviewUrls, setReviewUrls] = useState({ google_review_url: '', trustpilot_url: '' });
  const [savingUrls, setSavingUrls] = useState(false);
  const [brandingCompanies, setBrandingCompanies] = useState([]);
  const [selectedBrandingCompany, setSelectedBrandingCompany] = useState(null);
  const [brandingForm, setBrandingForm] = useState({
    slug: '',
    logo_url: '',
    title: '',
    description: '',
    primary_color: '#f97316',
    header_color: '#ffffff',
    background_color: '#f8fafc',
    background_image_url: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    email: '',
    password: '',
    contact_name: '',
    subscription_months: 12,
    slug: '',
  });
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPages, setAnalyticsPages] = useState([]);
  const [analyticsTimeline, setAnalyticsTimeline] = useState([]);
  const [analyticsLogins, setAnalyticsLogins] = useState([]);
  const [analyticsSuspicious, setAnalyticsSuspicious] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    const info = localStorage.getItem('admin_info');
    if (!token || !info) {
      navigate('/admin');
      return;
    }
    const parsedInfo = JSON.parse(info);
    setAdminInfo(parsedInfo);
    fetchData(parsedInfo);
  }, [token, navigate]);

  const fetchData = async (adminInfoParam = null) => {
    setLoading(true);
    const currentAdminInfo = adminInfoParam || adminInfo;
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch submissions
      try {
        const subRes = await fetch(`${API_URL}/api/submissions`, { headers });
        if (subRes.ok) {
          const subData = await subRes.json();
          setSubmissions(subData.submissions || []);
        } else if (subRes.status === 401) {
          // Token expired, redirect to login
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_info');
          navigate('/admin');
          return;
        }
      } catch (e) {
        console.warn('Could not fetch submissions:', e);
      }

      // Fetch admins
      try {
        const adminRes = await fetch(`${API_URL}/api/admin/list`, { headers });
        if (adminRes.ok) {
          const adminData = await adminRes.json();
          setAdmins(adminData.admins || []);
        }
      } catch (e) {
        console.warn('Could not fetch admins:', e);
      }

      // Fetch commercial users
      try {
        const usersRes = await fetch(`${API_URL}/api/users/list`, { headers });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.users || []);
        }
      } catch (e) {
        console.warn('Could not fetch users:', e);
      }

      // Fetch review URLs
      if (currentAdminInfo?.company_id) {
        try {
          const urlsRes = await fetch(`${API_URL}/api/company/review-urls/${currentAdminInfo.company_id}`);
          if (urlsRes.ok) {
            const urlsData = await urlsRes.json();
            setReviewUrls(urlsData);
          }
        } catch (e) {
          console.warn('Could not fetch review URLs:', e);
        }
      }

      // Fetch all companies with branding (super_admin only)
      if (currentAdminInfo?.role === 'super_admin') {
        try {
          const brandingRes = await fetch(`${API_URL}/api/company/all-branding`, { headers });
          if (brandingRes.ok) {
            const brandingData = await brandingRes.json();
            setBrandingCompanies(brandingData.companies || []);
          }
        } catch (e) {
          console.warn('Could not fetch branding companies:', e);
        }
      }

    } catch (error) {
      console.error('Fetch error:', error);
      // Don't show error toast for minor issues
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (adminInfo?.role !== 'super_admin') return;
    
    setLoadingAnalytics(true);
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      const [overviewRes, pagesRes, timelineRes, loginsRes, suspiciousRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/overview`, { headers }),
        fetch(`${API_URL}/api/analytics/pages`, { headers }),
        fetch(`${API_URL}/api/analytics/timeline?days=30`, { headers }),
        fetch(`${API_URL}/api/analytics/logins?limit=30`, { headers }),
        fetch(`${API_URL}/api/analytics/suspicious`, { headers }),
      ]);
      
      if (overviewRes.ok) setAnalyticsData(await overviewRes.json());
      if (pagesRes.ok) setAnalyticsPages((await pagesRes.json()).pages || []);
      if (timelineRes.ok) setAnalyticsTimeline((await timelineRes.json()).timeline || []);
      if (loginsRes.ok) setAnalyticsLogins((await loginsRes.json()).logins || []);
      if (suspiciousRes.ok) setAnalyticsSuspicious((await suspiciousRes.json()).suspicious_ips || []);
    } catch (e) {
      console.warn('Could not fetch analytics:', e);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    toast.success('Déconnexion réussie');
    navigate('/admin');
  };

  const handleDownloadExcel = async () => {
    try {
      toast.info('Génération du fichier Excel...');
      const response = await fetch(`${API_URL}/api/export/excel`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur export');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soumissions_litige_protect_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Fichier Excel téléchargé !');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(error.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Supprimer cette soumission ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Soumission supprimée');
        fetchData();
      } else {
        throw new Error('Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = async (submissionId, clientName) => {
    try {
      toast.info('Génération du PDF...');
      const response = await fetch(`${API_URL}/api/export/pdf/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur export PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDownloadVideo = async (videoId, clientName) => {
    try {
      toast.info('Téléchargement de la vidéo...');
      const response = await fetch(`${API_URL}/api/videos/file/${videoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur téléchargement vidéo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video_${clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.webm`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Vidéo téléchargée !');
    } catch (error) {
      console.error('Video download error:', error);
      toast.error(error.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDownloadAll = async (submission) => {
    const clientName = `${submission.client_info?.nom}_${submission.client_info?.prenom}`;
    
    // Download PDF
    await handleDownloadPDF(submission.id, clientName);
    
    // Download Video if exists
    if (submission.video_data?.video_id && !submission.video_data?.skip_video) {
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
      await handleDownloadVideo(submission.video_data.video_id, clientName);
    }
  };

  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAdmin)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Admin créé avec succès');
        setShowAddAdmin(false);
        setNewAdmin({ email: '', password: '', name: '' });
        fetchData();
      } else {
        throw new Error(data.detail || 'Erreur création');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Supprimer cet administrateur ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Admin supprimé');
        fetchData();
      } else {
        throw new Error('Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/users/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Commercial créé avec succès');
        setShowAddUser(false);
        setNewUser({ email: '', password: '', name: '' });
        fetchData();
      } else {
        throw new Error(data.detail || 'Erreur création');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Supprimer ce commercial ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Commercial supprimé');
        fetchData();
      } else {
        throw new Error('Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveReviewUrls = async (e) => {
    e.preventDefault();
    setSavingUrls(true);
    try {
      const response = await fetch(`${API_URL}/api/company/review-urls`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewUrls)
      });
      if (response.ok) {
        toast.success('URLs des plateformes d\'avis mises à jour');
      } else {
        throw new Error('Erreur sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingUrls(false);
    }
  };

  // Branding functions
  const handleSelectBrandingCompany = (company) => {
    setSelectedBrandingCompany(company);
    const b = company.branding || {};
    setBrandingForm({
      slug: company.slug || '',
      logo_url: b.logo_url || '',
      title: b.title || company.name || '',
      description: b.description || '',
      primary_color: b.primary_color || '#f97316',
      header_color: b.header_color || '#ffffff',
      background_color: b.background_color || '#f8fafc',
      background_image_url: b.background_image_url || '',
    });
  };

  const handleUploadBrandingImage = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/company/branding/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) throw new Error('Erreur upload');

      const data = await response.json();
      setBrandingForm(prev => ({ ...prev, [fieldName]: data.url }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    if (!selectedBrandingCompany) return;

    setSavingBranding(true);
    try {
      const response = await fetch(`${API_URL}/api/company/branding/${selectedBrandingCompany.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slug: brandingForm.slug,
          branding: {
            logo_url: brandingForm.logo_url,
            title: brandingForm.title,
            description: brandingForm.description,
            primary_color: brandingForm.primary_color,
            header_color: brandingForm.header_color,
            background_color: brandingForm.background_color,
            background_image_url: brandingForm.background_image_url,
          }
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur sauvegarde');
      }

      toast.success('Personnalisation enregistrée');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingBranding(false);
    }
  };

  const copyBrandedUrl = () => {
    if (!brandingForm.slug) return;
    const url = `${window.location.origin}/e/${brandingForm.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name || !newCompany.email || !newCompany.password || !newCompany.contact_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setCreatingCompany(true);
    try {
      const response = await fetch(`${API_URL}/api/company/create-manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCompany)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur création');
      }
      
      toast.success(`Entreprise "${newCompany.name}" créée avec succès !`);
      setShowAddCompany(false);
      setNewCompany({
        name: '',
        email: '',
        password: '',
        contact_name: '',
        subscription_months: 12,
        slug: '',
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    } finally {
      setCreatingCompany(false);
    }
  };

  // Filter submissions based on search
  const filterSubmissions = (subs) => {
    return subs.filter(sub => {
      const client = sub.client_info || {};
      const searchLower = searchTerm.toLowerCase();
      return (
        (client.nom || '').toLowerCase().includes(searchLower) ||
        (client.prenom || '').toLowerCase().includes(searchLower) ||
        (client.email || '').toLowerCase().includes(searchLower) ||
        (client.ville || '').toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredSubmissions = filterSubmissions(submissions);

  // Reusable function to render submissions list
  const renderSubmissionsList = (submissionsList) => {
    const filtered = filterSubmissions(submissionsList);
    
    if (filtered.length === 0) {
      return (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <FileText className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
          <p className="text-foreground/60">Aucun dossier trouvé</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {filtered.map((sub) => {
          const dt = formatDateTime(sub.created_at);
          const clientName = `${sub.client_info?.nom}_${sub.client_info?.prenom}`;
          const hasVideo = sub.video_data?.video_id && !sub.video_data?.skip_video;
          
          return (
            <div key={sub.id} className="bg-card border border-border rounded-lg overflow-hidden">
              {/* Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30"
                onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">
                      {sub.client_info?.civilite} {sub.client_info?.nom} {sub.client_info?.prenom}
                    </p>
                    {hasVideo && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Vidéo</span>
                    )}
                  </div>
                  <p className="text-sm text-foreground/60 truncate">
                    {sub.client_info?.email} • {sub.client_info?.ville}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <div className="hidden sm:flex flex-col items-end text-xs text-foreground/50">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {dt.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {dt.time}
                    </span>
                  </div>
                  {expandedSubmission === sub.id ? (
                    <ChevronUp className="w-5 h-5 text-foreground/40" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-foreground/40" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedSubmission === sub.id && (
                <div className="border-t border-border p-4 bg-secondary/10 space-y-4">
                  {/* Date/Heure Mobile */}
                  <div className="sm:hidden bg-accent/10 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-sm font-medium">Enregistré le :</span>
                    <span className="text-sm">{dt.date} à {dt.time}</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-foreground/60 mb-1">Contact</p>
                      <p>📧 {sub.client_info?.email}</p>
                      <p>📱 {sub.client_info?.portable1}</p>
                      <p>📍 {sub.client_info?.adresse}, {sub.client_info?.code_postal} {sub.client_info?.ville}</p>
                      <p>🏢 {sub.client_info?.societe_installatrice}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground/60 mb-1">Validation</p>
                      <p>📋 Type: {sub.form_validation?.form_type === 'commercial' ? 'Commercial (19 affirmations)' : 'Installation (9 affirmations)'}</p>
                      <p>🎥 Vidéo: {sub.video_data?.skip_video ? '❌ Non' : '✅ Oui'}</p>
                      <p>✍️ Signature: {sub.signature_data?.sms_verified ? '✅ Validée' : '❌ Non'}</p>
                      {sub.signature_data?.gps_location && (
                        <p>📍 GPS: {sub.signature_data.gps_location.latitude?.toFixed(4)}, {sub.signature_data.gps_location.longitude?.toFixed(4)}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Signature Image */}
                  {sub.signature_data?.signature_data && (
                    <div>
                      <p className="font-medium text-foreground/60 mb-2">Signature</p>
                      <div className="bg-white border rounded-lg p-2 inline-block">
                        <img 
                          src={sub.signature_data.signature_data} 
                          alt="Signature"
                          className="h-20 object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Video intégrée dans le dossier */}
                  {hasVideo && (
                    <div>
                      <p className="font-medium text-foreground/60 mb-2">Vidéo Témoignage</p>
                      <div className="bg-black rounded-lg overflow-hidden aspect-video max-w-md">
                        <video
                          src={`${API_URL}/api/videos/file/${sub.video_data.video_id}`}
                          controls
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Avis Client */}
                  <div>
                    <p className="font-medium text-foreground/60 mb-2">Avis Client</p>
                    {sub.avis_data?.skip_avis ? (
                      <p className="text-sm text-foreground/50 italic">Pas d'avis laissé</p>
                    ) : sub.avis_data?.avis_submitted ? (
                      <div className="bg-white border border-border rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Note :</span>
                          <span className="text-accent">
                            {'⭐'.repeat(sub.avis_data?.rating || 0)}
                          </span>
                          <span className="text-sm text-foreground/60">({sub.avis_data?.rating || 0}/5)</span>
                        </div>
                        {sub.avis_data?.avis_text && (
                          <div>
                            <p className="text-sm font-medium text-foreground/60 mb-1">Témoignage :</p>
                            <p className="text-sm text-foreground/80 italic bg-secondary/30 p-2 rounded">
                              "{sub.avis_data.avis_text}"
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground/50 italic">Aucun avis enregistré</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(sub.id, clientName);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm hover:bg-accent/90"
                    >
                      <FileDown className="w-4 h-4" />
                      PDF
                    </button>
                    {hasVideo && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadVideo(sub.video_data.video_id, clientName);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                      >
                        <Video className="w-4 h-4" />
                        Vidéo
                      </button>
                    )}
                    {hasVideo && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAll(sub);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                      >
                        <Download className="w-4 h-4" />
                        Tout télécharger
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteSubmission(sub.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-destructive/10 text-destructive rounded-md text-sm hover:bg-destructive/20"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-accent animate-spin mx-auto mb-4" />
          <p className="text-foreground/60">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <h1 className="text-lg sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Litige-Protect <span className="text-accent">Admin</span>
          </h1>
          {adminInfo?.company_name && (
            <span className="hidden sm:inline-flex items-center px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
              {adminInfo.company_name}
            </span>
          )}
          {adminInfo?.role === 'super_admin' && (
            <span className="hidden sm:inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
              Super Admin
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="hidden sm:block text-sm text-foreground/60">
            {adminInfo?.name}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      {/* Company Info Banner (for company admins) */}
      {adminInfo?.company_name && adminInfo?.role !== 'super_admin' && (
        <div className="bg-accent/5 border-b border-accent/20 px-4 sm:px-6 py-2">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-accent" />
            <span className="font-medium">{adminInfo.company_name}</span>
            <span className="text-foreground/60">• Espace dédié à votre entreprise</span>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="bg-card border-b px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto grid grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-blue-600">
              {submissions.filter(s => s.form_validation?.form_type === 'commercial').length}
            </p>
            <p className="text-xs sm:text-sm text-foreground/60">Commercial</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-green-600">
              {submissions.filter(s => s.form_validation?.form_type === 'installation').length}
            </p>
            <p className="text-xs sm:text-sm text-foreground/60">Installation</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-accent">
              {submissions.filter(s => s.avis_data?.avis_submitted).length}
            </p>
            <p className="text-xs sm:text-sm text-foreground/60">Avis</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-accent">{admins.length}</p>
            <p className="text-xs sm:text-sm text-foreground/60">Admins</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-bold text-orange-600">{users.length}</p>
            <p className="text-xs sm:text-sm text-foreground/60">Commerciaux</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex gap-1 sm:gap-2 overflow-x-auto">
          {[
            { id: 'commercial', label: 'Commercial', icon: Briefcase, count: submissions.filter(s => s.form_validation?.form_type === 'commercial').length },
            { id: 'installation', label: 'Après Installation', icon: Wrench, count: submissions.filter(s => s.form_validation?.form_type === 'installation').length },
            { id: 'avis', label: 'Avis Clients', icon: Star },
            { id: 'commerciaux', label: 'Commerciaux', icon: UserPlus, count: users.length },
            { id: 'settings', label: 'Paramètres', icon: Settings },
            ...(adminInfo?.role === 'super_admin' ? [
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'branding', label: 'Personnalisation', icon: Palette, count: brandingCompanies.length }
            ] : []),
            { id: 'admins', label: 'Admins', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'analytics') fetchAnalytics();
              }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-foreground/60 hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-accent/20' : 'bg-secondary'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Commercial Tab */}
        {activeTab === 'commercial' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold">Dossiers Commercial (19 affirmations)</h2>
            </div>
            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:outline-none text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Excel
                </button>
              </div>
            </div>

            {/* Commercial Submissions List */}
            {renderSubmissionsList(submissions.filter(s => s.form_validation?.form_type === 'commercial'))}
          </div>
        )}

        {/* Installation Tab */}
        {activeTab === 'installation' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Wrench className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold">Dossiers Après Installation (9 affirmations)</h2>
            </div>
            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:outline-none text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Excel
                </button>
              </div>
            </div>

            {/* Installation Submissions List */}
            {renderSubmissionsList(submissions.filter(s => s.form_validation?.form_type === 'installation'))}
          </div>
        )}

        {/* Avis Tab */}
        {activeTab === 'avis' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Avis et Témoignages Clients</h2>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-accent">
                  {submissions.filter(s => s.avis_data?.avis_submitted).length}
                </p>
                <p className="text-xs text-foreground/60">Avis reçus</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-accent">
                  {submissions.filter(s => s.avis_data?.rating === 5).length}
                </p>
                <p className="text-xs text-foreground/60">5 étoiles</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-accent">
                  {submissions.filter(s => s.avis_data?.skip_avis).length}
                </p>
                <p className="text-xs text-foreground/60">Sans avis</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-accent">
                  {submissions.filter(s => s.avis_data?.avis_submitted).length > 0
                    ? (submissions.filter(s => s.avis_data?.avis_submitted).reduce((acc, s) => acc + (s.avis_data?.rating || 0), 0) / submissions.filter(s => s.avis_data?.avis_submitted).length).toFixed(1)
                    : '0'}
                </p>
                <p className="text-xs text-foreground/60">Note moyenne</p>
              </div>
            </div>

            {/* Avis List */}
            {submissions.filter(s => s.avis_data?.avis_submitted).length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Star className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
                <p className="text-foreground/60">Aucun avis client enregistré</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.filter(s => s.avis_data?.avis_submitted).map((sub) => (
                  <div key={sub.id} className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium">
                          {sub.client_info?.civilite} {sub.client_info?.nom} {sub.client_info?.prenom}
                        </p>
                        <p className="text-sm text-foreground/60">{sub.client_info?.ville}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= (sub.avis_data?.rating || 0)
                                  ? 'text-accent fill-accent'
                                  : 'text-foreground/20'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-foreground/40 mt-1">
                          {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    {sub.avis_data?.avis_text && (
                      <div className="bg-secondary/30 rounded-lg p-3">
                        <p className="text-sm text-foreground/80 italic">
                          "{sub.avis_data.avis_text}"
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Commerciaux Tab */}
        {activeTab === 'commerciaux' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" data-testid="commerciaux-tab-title">Gestion des Commerciaux</h2>
              {adminInfo?.role !== 'super_admin' && (
                <button
                  data-testid="add-commercial-btn"
                  onClick={() => setShowAddUser(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              )}
            </div>

            {adminInfo?.role === 'super_admin' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-foreground/80">
                Les commerciaux sont créés par les administrateurs d'entreprise depuis leur espace.
              </div>
            )}

            {/* Add User Form */}
            {showAddUser && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-medium mb-4">Nouveau commercial</h3>
                <form onSubmit={handleAddUser} className="space-y-3">
                  <input
                    data-testid="new-commercial-name"
                    type="text"
                    placeholder="Nom complet"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <input
                    data-testid="new-commercial-email"
                    type="email"
                    placeholder="Email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <input
                    data-testid="new-commercial-password"
                    type="password"
                    placeholder="Mot de passe"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid="submit-new-commercial"
                      type="submit"
                      className="flex-1 h-10 bg-accent text-accent-foreground rounded-md text-sm"
                    >
                      Créer
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddUser(false)}
                      className="flex-1 h-10 bg-secondary text-secondary-foreground rounded-md text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Users List */}
            {users.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <UserPlus className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
                <p className="text-foreground/60">Aucun commercial enregistré</p>
                {adminInfo?.role !== 'super_admin' && (
                  <p className="text-sm text-foreground/40 mt-2">
                    Cliquez sur "Ajouter" pour créer un compte commercial
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between" data-testid={`commercial-row-${user.id}`}>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-foreground/60">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                          Commercial
                        </span>
                        {user.company_name && (
                          <span className="text-xs text-foreground/40">{user.company_name}</span>
                        )}
                        <span className="text-xs text-foreground/40">
                          Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    <button
                      data-testid={`delete-commercial-${user.id}`}
                      onClick={() => handleDeleteUser(user.id)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-md"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && adminInfo?.role !== 'super_admin' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold" data-testid="settings-tab-title">Paramètres</h2>

            {/* Review URLs */}
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-accent" />
                Plateformes d'avis clients
              </h3>
              <p className="text-sm text-foreground/60 mb-4">
                Configurez vos liens Google Reviews et Trustpilot. Vos clients pourront publier leur avis directement sur ces plateformes.
              </p>

              <form onSubmit={handleSaveReviewUrls} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                      Lien Google Reviews
                    </span>
                  </label>
                  <input
                    data-testid="google-review-url-input"
                    type="url"
                    value={reviewUrls.google_review_url}
                    onChange={(e) => setReviewUrls({ ...reviewUrls, google_review_url: e.target.value })}
                    placeholder="https://g.page/r/votre-entreprise/review"
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                  />
                  <p className="text-xs text-foreground/40 mt-1">
                    Trouvez votre lien dans Google Business → Partager → Demander des avis
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#00B67A" d="M12 0L15.09 7.36L23 8.54L17.5 13.97L18.82 22L12 18.27L5.18 22L6.5 13.97L1 8.54L8.91 7.36L12 0Z"/></svg>
                      Lien Trustpilot
                    </span>
                  </label>
                  <input
                    data-testid="trustpilot-url-input"
                    type="url"
                    value={reviewUrls.trustpilot_url}
                    onChange={(e) => setReviewUrls({ ...reviewUrls, trustpilot_url: e.target.value })}
                    placeholder="https://fr.trustpilot.com/evaluate/votre-entreprise"
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                  />
                  <p className="text-xs text-foreground/40 mt-1">
                    Trouvez votre lien sur votre page Trustpilot Business
                  </p>
                </div>

                <button
                  data-testid="save-review-urls-btn"
                  type="submit"
                  disabled={savingUrls}
                  className="h-10 px-6 bg-accent text-accent-foreground rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50"
                >
                  {savingUrls ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'settings' && adminInfo?.role === 'super_admin' && (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Settings className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
            <p className="text-foreground/60">Les paramètres sont configurés par chaque entreprise depuis leur espace.</p>
          </div>
        )}

        {/* Analytics Tab (Super Admin only) */}
        {activeTab === 'analytics' && adminInfo?.role === 'super_admin' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-accent" />
                Tableau de bord Analytics
              </h2>
              <button
                onClick={fetchAnalytics}
                disabled={loadingAnalytics}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loadingAnalytics ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>

            {loadingAnalytics && !analyticsData ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-accent mb-4" />
                <p className="text-foreground/60">Chargement des analytics...</p>
              </div>
            ) : analyticsData ? (
              <>
                {/* Overview Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <TrendingUp className="w-4 h-4" />
                      Visites totales
                    </div>
                    <p className="text-2xl font-bold">{analyticsData.total_visits?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Aujourd'hui
                    </div>
                    <p className="text-2xl font-bold text-green-600">{analyticsData.today_visits?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <Users className="w-4 h-4" />
                      Visiteurs uniques (30j)
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{analyticsData.unique_ips_month?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <Shield className="w-4 h-4" />
                      Connexions réussies
                    </div>
                    <p className="text-2xl font-bold text-accent">{analyticsData.total_logins?.toLocaleString() || 0}</p>
                  </div>
                </div>

                {/* Second row - Login stats and device breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Connexions échouées
                    </div>
                    <p className="text-2xl font-bold text-red-600">{analyticsData.failed_logins?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <Smartphone className="w-4 h-4" />
                      Mobile
                    </div>
                    <p className="text-2xl font-bold">{analyticsData.mobile_visits?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-foreground/60 text-sm mb-1">
                      <Monitor className="w-4 h-4" />
                      Desktop
                    </div>
                    <p className="text-2xl font-bold">{analyticsData.desktop_visits?.toLocaleString() || 0}</p>
                  </div>
                </div>

                {/* Suspicious Activity Alert */}
                {analyticsSuspicious.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" />
                      Activité suspecte détectée ({analyticsSuspicious.length} IP)
                    </h3>
                    <div className="space-y-2">
                      {analyticsSuspicious.slice(0, 5).map((ip, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                          <span className="font-mono text-red-700">{ip._id}</span>
                          <span className="text-red-600 font-medium">{ip.failed_attempts} tentatives échouées</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Two columns: Pages and Recent Logins */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Pages */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Pages les plus visitées (30j)
                    </h3>
                    {analyticsPages.length === 0 ? (
                      <p className="text-foreground/60 text-sm">Aucune donnée disponible</p>
                    ) : (
                      <div className="space-y-2">
                        {analyticsPages.slice(0, 10).map((page, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="font-mono text-xs truncate flex-1">{page.endpoint}</span>
                            <div className="flex items-center gap-2 ml-2">
                              <span className="text-green-600">{page.success}</span>
                              <span className="text-red-600">{page.errors}</span>
                              <span className="font-medium">{page.visits}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent Logins */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Dernières connexions
                    </h3>
                    {analyticsLogins.length === 0 ? (
                      <p className="text-foreground/60 text-sm">Aucune donnée disponible</p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {analyticsLogins.map((login, idx) => (
                          <div key={idx} className={`flex items-center justify-between text-sm p-2 rounded ${login.status_code === 200 ? 'bg-green-50' : 'bg-red-50'}`}>
                            <div className="flex-1 truncate">
                              <span className="font-medium">{login.extra_data?.email || 'Inconnu'}</span>
                              <span className="text-xs text-foreground/50 ml-2">{login.ip_address}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${login.status_code === 200 ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                {login.status_code === 200 ? 'OK' : 'ÉCHEC'}
                              </span>
                              <span className="text-xs text-foreground/50">
                                {new Date(login.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline Chart (Simple) */}
                {analyticsTimeline.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Activité des 30 derniers jours
                    </h3>
                    <div className="flex items-end gap-1 h-32">
                      {analyticsTimeline.slice(-30).map((day, idx) => {
                        const maxVisits = Math.max(...analyticsTimeline.map(d => d.visits || 0), 1);
                        const height = ((day.visits || 0) / maxVisits) * 100;
                        return (
                          <div 
                            key={idx} 
                            className="flex-1 bg-accent/20 hover:bg-accent/40 rounded-t transition-colors cursor-pointer group relative"
                            style={{ height: `${Math.max(height, 4)}%` }}
                            title={`${day.date}: ${day.visits} visites`}
                          >
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                              {day.date}: {day.visits} visites
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-foreground/40 mt-2">
                      <span>{analyticsTimeline[0]?.date}</span>
                      <span>{analyticsTimeline[analyticsTimeline.length - 1]?.date}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <BarChart3 className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
                <p className="text-foreground/60">Cliquez sur "Actualiser" pour charger les données analytics</p>
              </div>
            )}
          </div>
        )}

        {/* Branding Tab (Super Admin only) */}
        {activeTab === 'branding' && adminInfo?.role === 'super_admin' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" data-testid="branding-tab-title">Personnalisation des Entreprises</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddCompany(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nouvelle entreprise
                </button>
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
              </div>
            </div>

            {/* Add Company Form */}
            {showAddCompany && (
              <div className="bg-card border-2 border-accent rounded-lg p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Créer une nouvelle entreprise</h3>
                  <button onClick={() => setShowAddCompany(false)} className="p-1 hover:bg-secondary rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleCreateCompany} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Nom de l'entreprise *</label>
                      <input
                        data-testid="new-company-name"
                        type="text"
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                        placeholder="Mon Entreprise SARL"
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Slug URL (optionnel)</label>
                      <input
                        data-testid="new-company-slug"
                        type="text"
                        value={newCompany.slug}
                        onChange={(e) => setNewCompany({ ...newCompany, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="mon-entreprise"
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                      />
                      <p className="text-xs text-foreground/40 mt-1">Laissez vide pour génération automatique</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Nom du contact *</label>
                      <input
                        data-testid="new-company-contact"
                        type="text"
                        value={newCompany.contact_name}
                        onChange={(e) => setNewCompany({ ...newCompany, contact_name: e.target.value })}
                        placeholder="Jean Dupont"
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email admin *</label>
                      <input
                        data-testid="new-company-email"
                        type="email"
                        value={newCompany.email}
                        onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                        placeholder="admin@entreprise.fr"
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Mot de passe *</label>
                      <input
                        data-testid="new-company-password"
                        type="password"
                        value={newCompany.password}
                        onChange={(e) => setNewCompany({ ...newCompany, password: e.target.value })}
                        placeholder="Mot de passe"
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Durée abonnement</label>
                      <select
                        data-testid="new-company-duration"
                        value={newCompany.subscription_months}
                        onChange={(e) => setNewCompany({ ...newCompany, subscription_months: parseInt(e.target.value) })}
                        className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                      >
                        <option value={1}>1 mois</option>
                        <option value={3}>3 mois</option>
                        <option value={6}>6 mois</option>
                        <option value={12}>1 an</option>
                        <option value={24}>2 ans</option>
                        <option value={36}>3 ans</option>
                        <option value={120}>10 ans (illimité)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <button
                      data-testid="submit-new-company"
                      type="submit"
                      disabled={creatingCompany}
                      className="flex-1 h-10 bg-accent text-accent-foreground rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50"
                    >
                      {creatingCompany ? 'Création...' : 'Créer l\'entreprise'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddCompany(false)}
                      className="px-6 h-10 bg-secondary text-secondary-foreground rounded-md text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Companies list */}
              <div className="lg:col-span-1 space-y-3">
                <h3 className="font-medium text-sm text-foreground/60">Sélectionner une entreprise</h3>
                {brandingCompanies.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <Building2 className="w-8 h-8 text-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-foreground/60">Aucune entreprise</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {brandingCompanies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => handleSelectBrandingCompany(company)}
                        data-testid={`branding-company-${company.id}`}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedBrandingCompany?.id === company.id
                            ? 'bg-accent/10 border-accent'
                            : 'bg-card border-border hover:bg-secondary/50'
                        }`}
                      >
                        <p className="font-medium truncate">{company.name}</p>
                        <p className="text-xs text-foreground/50 truncate">/{company.slug || 'non-défini'}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                          company.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {company.status === 'active' ? 'Actif' : 'En attente'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Branding form */}
              <div className="lg:col-span-2">
                {!selectedBrandingCompany ? (
                  <div className="bg-card border border-border rounded-lg p-8 text-center h-full flex flex-col items-center justify-center">
                    <Palette className="w-12 h-12 text-foreground/20 mb-4" />
                    <p className="text-foreground/60">Sélectionnez une entreprise pour personnaliser son apparence</p>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{selectedBrandingCompany.name}</h3>
                      <button
                        onClick={() => setSelectedBrandingCompany(null)}
                        className="p-1 hover:bg-secondary rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <form onSubmit={handleSaveBranding} className="space-y-5">
                      {/* Slug / URL */}
                      <div>
                        <label className="block text-sm font-medium mb-2">URL personnalisée (slug)</label>
                        <div className="flex gap-2">
                          <input
                            data-testid="branding-slug-input"
                            type="text"
                            value={brandingForm.slug}
                            onChange={(e) => setBrandingForm({ ...brandingForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                            placeholder="nom-entreprise"
                            className="flex-1 h-10 bg-white border border-input rounded-md px-4 text-sm"
                          />
                          <button
                            type="button"
                            onClick={copyBrandedUrl}
                            disabled={!brandingForm.slug}
                            className="px-3 h-10 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50"
                            title="Copier l'URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {brandingForm.slug && (
                            <a
                              href={`/e/${brandingForm.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 h-10 bg-accent/10 text-accent rounded-md hover:bg-accent/20 flex items-center"
                              title="Voir la page"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-foreground/40 mt-1">
                          URL finale : {window.location.origin}/e/{brandingForm.slug || 'slug'}
                        </p>
                      </div>

                      {/* Title & Description */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Titre affiché</label>
                          <input
                            data-testid="branding-title-input"
                            type="text"
                            value={brandingForm.title}
                            onChange={(e) => setBrandingForm({ ...brandingForm, title: e.target.value })}
                            placeholder="Nom de l'entreprise"
                            className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Description</label>
                          <input
                            data-testid="branding-description-input"
                            type="text"
                            value={brandingForm.description}
                            onChange={(e) => setBrandingForm({ ...brandingForm, description: e.target.value })}
                            placeholder="Courte description"
                            className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                          />
                        </div>
                      </div>

                      {/* Colors */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Couleur principale</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="color"
                              value={brandingForm.primary_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingForm.primary_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                              className="flex-1 h-10 bg-white border border-input rounded-md px-3 text-sm font-mono"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Couleur header</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="color"
                              value={brandingForm.header_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, header_color: e.target.value })}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingForm.header_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, header_color: e.target.value })}
                              className="flex-1 h-10 bg-white border border-input rounded-md px-3 text-sm font-mono"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Couleur de fond</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="color"
                              value={brandingForm.background_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, background_color: e.target.value })}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingForm.background_color}
                              onChange={(e) => setBrandingForm({ ...brandingForm, background_color: e.target.value })}
                              className="flex-1 h-10 bg-white border border-input rounded-md px-3 text-sm font-mono"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Logo Upload */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Logo</label>
                        <div className="flex gap-3 items-center">
                          {brandingForm.logo_url && (
                            <img
                              src={brandingForm.logo_url.startsWith('/') ? `${API_URL}${brandingForm.logo_url}` : brandingForm.logo_url}
                              alt="Logo"
                              className="h-12 w-12 object-contain rounded border bg-white"
                            />
                          )}
                          <label className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 cursor-pointer text-sm">
                            <Upload className="w-4 h-4" />
                            {uploadingImage ? 'Upload...' : 'Uploader un logo'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadBrandingImage(e, 'logo_url')}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                          </label>
                          {brandingForm.logo_url && (
                            <button
                              type="button"
                              onClick={() => setBrandingForm({ ...brandingForm, logo_url: '' })}
                              className="p-2 text-destructive hover:bg-destructive/10 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Background Image Upload */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Image de fond (optionnel)</label>
                        <div className="flex gap-3 items-center">
                          {brandingForm.background_image_url && (
                            <img
                              src={brandingForm.background_image_url.startsWith('/') ? `${API_URL}${brandingForm.background_image_url}` : brandingForm.background_image_url}
                              alt="Background"
                              className="h-12 w-20 object-cover rounded border"
                            />
                          )}
                          <label className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 cursor-pointer text-sm">
                            <Upload className="w-4 h-4" />
                            {uploadingImage ? 'Upload...' : 'Uploader un fond'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadBrandingImage(e, 'background_image_url')}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                          </label>
                          {brandingForm.background_image_url && (
                            <button
                              type="button"
                              onClick={() => setBrandingForm({ ...brandingForm, background_image_url: '' })}
                              className="p-2 text-destructive hover:bg-destructive/10 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="pt-4 border-t flex justify-end">
                        <button
                          data-testid="save-branding-btn"
                          type="submit"
                          disabled={savingBranding}
                          className="px-6 h-10 bg-accent text-accent-foreground rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50 flex items-center gap-2"
                        >
                          {savingBranding ? 'Enregistrement...' : 'Enregistrer la personnalisation'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Administrateurs</h2>
              <button
                onClick={() => setShowAddAdmin(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>

            {/* Add Admin Form */}
            {showAddAdmin && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-medium mb-4">Nouvel administrateur</h3>
                <form onSubmit={handleAddAdmin} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Nom"
                    value={newAdmin.name}
                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Mot de passe"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    className="w-full h-10 bg-white border border-input rounded-md px-4 text-sm"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 h-10 bg-accent text-accent-foreground rounded-md text-sm"
                    >
                      Créer
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddAdmin(false)}
                      className="flex-1 h-10 bg-secondary text-secondary-foreground rounded-md text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Admins List */}
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-sm text-foreground/60">{admin.email}</p>
                    <p className="text-xs text-foreground/40">{admin.role} • Créé le {new Date(admin.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  {admin.role !== 'super_admin' && (
                    <button
                      onClick={() => handleDeleteAdmin(admin.id)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-md"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Video Modal */}
      {videoModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setVideoModal(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setVideoModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-white/80"
            >
              <X className="w-8 h-8" />
            </button>
            <video
              src={videoModal}
              controls
              autoPlay
              className="w-full rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
